import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HeartIcon, MessageCircleIcon, PauseIcon, PlayIcon, SendIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { brand, reelClips } from '../data/content';
const CLIP_MS = 4200;
export function HeroReel() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef<number>(0);
  const clip = reelClips[index];
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    startedAt.current = performance.now() - progress * CLIP_MS;
    const tick = (now: number) => {
      const ratio = Math.min((now - startedAt.current) / CLIP_MS, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        setProgress(0);
        setIndex(i => (i + 1) % reelClips.length);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, index]);
  const goTo = (i: number) => {
    setProgress(0);
    setIndex(i);
  };
  return <div className="relative mx-auto w-full max-w-[19rem] sm:max-w-[21rem]" data-id="element-142" data-mp="3m753s694v43z">
      <div className="pearl-surface absolute -inset-3 -z-10 rounded-[2.75rem] border border-butter-100 dark:border-butter-700/40" data-id="element-143" data-mp="dpr40xxxtw4d" />

      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[2rem] bg-slate-900" data-id="element-144" data-mp="hsne209ewjwp">
        <AnimatePresence initial={false} data-id="element-145" data-mp="3xl9phk7r2m0">
          <motion.img key={clip.id} src={clip.src} alt={clip.alt} initial={{
          opacity: 0,
          scale: reduceMotion ? 1 : 1.08
        }} animate={{
          opacity: 1,
          scale: reduceMotion ? 1 : 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 0.5
          },
          scale: {
            duration: CLIP_MS / 1000 + 1,
            ease: 'linear'
          }
        }} className="absolute inset-0 h-full w-full object-cover" loading="eager" data-id="element-146" data-mp="8xh4cq70thow" />
        </AnimatePresence>

        {/* Legibility scrim */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/40" data-id="element-147" data-mp="web0js3380iq" />

        {/* Progress bars */}
        <div className="absolute inset-x-3 top-3 flex gap-1.5" data-id="element-148" data-mp="sdr5qv5zl8zr">
          {reelClips.map((c, i) => <button key={c.id} type="button" onClick={() => goTo(i)} aria-label={`Play clip ${i + 1}: ${c.caption}`} className="group h-1.5 flex-1 rounded-full bg-white/30" data-id="element-149" data-mp="nj4r8j4lz0bg" data-mpi={i}>
              <span className="block h-full rounded-full bg-butter-300 transition-[width] duration-100 ease-linear" style={{
            width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%'
          }} data-id="element-150" data-mp="dol65638lgrc" />
            </button>)}
        </div>

        {/* Account row */}
        <div className="absolute inset-x-4 top-8 flex items-center gap-2.5" data-id="element-151" data-mp="2g8nlsq24r0jp">
          <span className="pearl-surface flex h-8 w-8 items-center justify-center rounded-full border border-butter-200/70" data-id="element-152" data-mp="2kar6m5aovhhn">
            <span className="font-display text-xs font-semibold text-slate-800" data-id="element-153" data-mp="2pbopyl7fqhkq">ee</span>
          </span>
          <span className="text-sm font-semibold text-white drop-shadow" data-id="element-154" data-mp="2u85a74bikbsj">{brand.name}</span>
          <span className="rounded-full border border-white/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-id="element-155" data-mp="1wn0y26s6kty7">
            Follow
          </span>
        </div>

        {/* Side actions */}
        <div className="absolute bottom-24 right-3 flex flex-col items-center gap-4" data-id="element-156" data-mp="26iksspv0dmld">
          <button type="button" onClick={() => setLiked(v => !v)} aria-pressed={liked} aria-label={liked ? 'Unlike this clip' : 'Like this clip'} className="flex flex-col items-center gap-1 text-white transition-transform duration-200 hover:scale-110" data-id="element-157" data-mp="2bbduf4v6ket8">
            <HeartIcon className={`h-6 w-6 drop-shadow ${liked ? 'fill-butter-300 text-butter-300' : ''}`} data-id="element-158" data-mp="3kj7fp0udybtj" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-159" data-mp="3gif5o5dow2w7">Like</span>
          </button>
          <span className="flex flex-col items-center gap-1 text-white" data-id="element-160" data-mp="3u9tp2dbie63s">
            <MessageCircleIcon className="h-6 w-6 drop-shadow" aria-hidden="true" data-id="element-161" data-mp="3ub4v6jsffpy2" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-162" data-mp="3pgh207mha7d5">Comment</span>
          </span>
          <span className="flex flex-col items-center gap-1 text-white" data-id="element-163" data-mp="846k3h9l4cdj">
            <SendIcon className="h-6 w-6 drop-shadow" aria-hidden="true" data-id="element-164" data-mp="327gc69xndg2" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-165" data-mp="h30cswjsoilt">Share</span>
          </span>
        </div>

        {/* Caption */}
        <div className="absolute inset-x-4 bottom-4" data-id="element-166" data-mp="c46wkwd4idhy">
          <AnimatePresence mode="wait" initial={false} data-id="element-167" data-mp="2itli7zp1o8mb">
            <motion.p key={clip.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="max-w-[15rem] text-base font-semibold leading-snug text-white drop-shadow" data-id="element-168" data-mp="2iw7przbxqo4j">
              {clip.caption}
            </motion.p>
          </AnimatePresence>

          <div className="mt-3 flex items-center gap-2" data-id="element-169" data-mp="om18wws9n9z5">
            <button type="button" onClick={() => setPlaying(v => !v)} aria-label={playing ? 'Pause reel' : 'Play reel'} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-170" data-mp="okgdl4y4nn08">
              {playing ? <PauseIcon className="h-4 w-4" data-id="element-171" data-mp="1n51u58yisgd" /> : <PlayIcon className="h-4 w-4" data-id="element-172" data-mp="1nwogqkktfui" />}
            </button>
            <button type="button" onClick={() => setMuted(v => !v)} aria-label={muted ? 'Unmute reel' : 'Mute reel'} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-173" data-mp="5o6shxot5w6q">
              {muted ? <VolumeXIcon className="h-4 w-4" data-id="element-174" data-mp="38ywhk8c6j5oo" /> : <Volume2Icon className="h-4 w-4" data-id="element-175" data-mp="38zy1z3f354j2" />}
            </button>
          </div>
        </div>
      </div>

      {/* Floating proof card */}
      <div className="absolute -bottom-6 -left-3 w-56 rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:-left-8" style={{
      width: "269px",
      height: "76px",
      paddingTop: "17px"
    }} data-id="element-176" data-mp="2zxf12ouv11zg">
        <p className="text-sm font-semibold text-slate-900 dark:text-pearl-50" data-id="element-177" data-mp="3roctdrtbekke">
          Early Talent Communication 
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400" data-id="element-178" data-mp="3npn87d21byq7">
          Emberly Johnston, Coach
        </p>
      </div>
    </div>;
}